import { accomTypes, getCampingPriceFromAllocations } from '../BookingFieldset/helpers';

export { getCampingPriceFromAllocations };

export const getCampingAccomOptions = (packageData) =>
  packageData?.accommodationOptions?.filter((ao) => ao.accommodationType === accomTypes.CAMPING) ||
  [];

export const getSelectedCamping = (state) =>
  (state?.accommodations || []).find((a) => a.accommodationType === accomTypes.CAMPING) || null;

export const getCampsiteOptions = (campingAccomGroup) => {
  if (!campingAccomGroup) return [];
  const { defaultOption, upgrades = [] } = campingAccomGroup;
  return [defaultOption, ...upgrades].filter(Boolean);
};

export const getCampsite = (campingAccomGroup, optionKey) =>
  getCampsiteOptions(campingAccomGroup).find((c) => c.option?.key === optionKey) || null;

export const getPitchesForCampsite = (campingAccomGroup, optionKey) =>
  getCampsite(campingAccomGroup, optionKey)?.pitches || [];

export const getPitch = (campingAccomGroup, optionKey, pitchKey) =>
  getPitchesForCampsite(campingAccomGroup, optionKey).find((p) => p.option?.key === pitchKey) ||
  null;

export const getTentsForPitch = (campingAccomGroup, optionKey, pitchKey) =>
  getPitch(campingAccomGroup, optionKey, pitchKey)?.tents || [];

export const getTent = (campingAccomGroup, optionKey, pitchKey, tentKey) =>
  getTentsForPitch(campingAccomGroup, optionKey, pitchKey).find((t) => t.option?.key === tentKey) ||
  null;

export const buildDefaultCampingSelection = (campingAccomGroup) => {
  if (!campingAccomGroup) return null;
  const campsites = getCampsiteOptions(campingAccomGroup);
  const campsite = campsites[0];
  if (!campsite) return null;

  const pitches = campsite.pitches || [];
  const shouldAutoPickPitch = campsites.length === 1 && pitches.length === 1;

  return {
    accommodationType: accomTypes.CAMPING,
    groupKey: campingAccomGroup.key,
    optionKey: campsite.option?.key,
    pitchKey: shouldAutoPickPitch ? pitches[0].option?.key : '',
    rooms: [],
  };
};

export const selectCampsite = (selection, campingAccomGroup, optionKey) => {
  const campsite = getCampsite(campingAccomGroup, optionKey);
  const pitches = campsite?.pitches || [];
  return {
    ...selection,
    optionKey,
    pitchKey: pitches.length === 1 ? pitches[0].option?.key : '',
    rooms: [],
  };
};

export const selectPitch = (selection, pitchKey) => ({
  ...selection,
  pitchKey,
  rooms: [],
});

export const addTent = (selection, tentKey) => {
  const rooms = selection.rooms || [];
  if (rooms.some((r) => r.roomKey === tentKey)) return selection;
  return {
    ...selection,
    rooms: [...rooms, { roomKey: tentKey, roomCount: 1 }],
  };
};

export const setTentCount = (selection, tentKey, count) => {
  const rooms = selection.rooms || [];
  if (count <= 0) {
    return {
      ...selection,
      rooms: rooms.filter((r) => r.roomKey !== tentKey),
    };
  }
  if (!rooms.some((r) => r.roomKey === tentKey)) {
    return {
      ...selection,
      rooms: [...rooms, { roomKey: tentKey, roomCount: count }],
    };
  }
  return {
    ...selection,
    rooms: rooms.map((r) => (r.roomKey === tentKey ? { ...r, roomCount: count } : r)),
  };
};

export const removeTent = (selection, tentKey) => ({
  ...selection,
  rooms: (selection.rooms || []).filter((r) => r.roomKey !== tentKey),
});

// ------- Pricing -------

export const TENT_PRICING_MODE = {
  PER_PERSON: 1,
  PER_UNIT: 2,
};

export const getTentPrice = (tent, currency, tentPricingMode, paxSelection, paxTypes) => {
  if (!tent) return 0;
  if (tentPricingMode === TENT_PRICING_MODE.PER_UNIT) {
    const entry = (tent.unitPricePerCurrency || []).find((p) => p.currency === currency);
    return entry?.price || 0;
  }
  const firstPaxKey = (paxTypes || [])[0]?.key;
  const firstPaxEntry = (tent.pricePerPax || []).find((p) => p.paxKey === firstPaxKey);
  const firstPriceEntry = (firstPaxEntry?.pricePerCurrency || []).find(
    (p) => p.currency === currency
  );
  return firstPriceEntry?.price || 0;
};

export const getCheapestTentPrice = (tents, currency, tentPricingMode, paxSelection, paxTypes) => {
  if (!tents?.length) return 0;
  return Math.min(
    ...tents.map((t) => getTentPrice(t, currency, tentPricingMode, paxSelection, paxTypes))
  );
};

export const getPitchFromPrice = (pitch, currency, tentPricingMode, paxSelection, paxTypes) =>
  getCheapestTentPrice(pitch?.tents || [], currency, tentPricingMode, paxSelection, paxTypes);

export const getCampsiteFromPrice = (
  campsite,
  currency,
  tentPricingMode,
  paxSelection,
  paxTypes
) => {
  const pitchPrices = (campsite?.pitches || []).map((p) =>
    getPitchFromPrice(p, currency, tentPricingMode, paxSelection, paxTypes)
  );
  return pitchPrices.length ? Math.min(...pitchPrices) : 0;
};
